// export default Overview;
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import metricsData from '../../../../Patient/Extras/swimscore_metrics.json';
import './Overview.scss';

// ---------- Helper UI ----------
const TrendIndicator = ({ trend }) => <span className={`trend-indicator ${trend}`}>{trend === 'up' ? '↑' : trend === 'down' ? '↓' : '•'}</span>;
TrendIndicator.propTypes = { trend: PropTypes.string };

// ---------- SwimScore track (reusable) ----------
const SwimScoreTrack = ({
  currentScore,
  previousScore,
  min = 0,
  max = 100,
  whoCutoff = 60,
  showEdges = true,
  showCurrentNumber = true,
  showPreviousNumber = true,
  heightPx = 8,
}) => {
  const norm = (v) => {
    if (!Number.isFinite(v)) return 0;
    if (max === min) return 0;
    const p = ((v - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, p));
  };
  const currPos = norm(currentScore);
  const hasPrev = previousScore !== null && previousScore !== undefined;
  const prevPos = norm(previousScore);
  const cutoffPos = Number.isFinite(whoCutoff) ? norm(whoCutoff) : null;
  // Show "min" for first test, "WHO" for multiple tests
  const isFirstTest = !hasPrev;

  return (
    <div className="swimscore-track" style={{ height: `${heightPx}px` }}>
      <div className="swimscore-rail position-relative">
        <div className="blue-track position-absolute"></div>
      </div>

      {cutoffPos != null && cutoffPos > 0 && cutoffPos < 100 && (
        <>
          <div className="who-tick" style={{ left: `${cutoffPos}%` }} aria-hidden="true" />
          <div className="who-label-container" style={{ left: `${cutoffPos}%` }}>
            {isFirstTest ? <span className="who-label-text">min</span> : <span className="who-label-text">WHO</span>}
          </div>
        </>
      )}

      {hasPrev && (
        <>
          <div className="score-marker prev" style={{ left: `${prevPos}%` }} aria-label={`Previous: ${Math.round(previousScore)}`} />
          {showPreviousNumber && (
            <div className="score-number" style={{ left: `${prevPos}%` }}>
              {Math.round(previousScore)}
            </div>
          )}
        </>
      )}

      <div className="score-marker current" style={{ left: `${currPos}%` }} aria-label={`Current: ${Math.round(currentScore)}`} />
      {showCurrentNumber && (
        <div className="score-number" style={{ left: `${currPos}%` }}>
          {Math.round(currentScore)}
        </div>
      )}

      {showEdges && (
        <>
          <div className="rail-edge left">{min}</div>
          <div className="rail-edge right">{max}</div>
        </>
      )}
    </div>
  );
};
SwimScoreTrack.propTypes = {
  currentScore: PropTypes.number.isRequired,
  previousScore: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  whoCutoff: PropTypes.number,
  showEdges: PropTypes.bool,
  showCurrentNumber: PropTypes.bool,
  showPreviousNumber: PropTypes.bool,
  heightPx: PropTypes.number,
};

// ---------- name → JSON metric mapper ----------
const jsonMetricKeyByDisplay = {
  'Effective Swimmers': 'Total Motility (%)', // keep as before; change if your JSON uses a different row
  'Strong Forward Swimmers': 'Vitality (Live %, Eosin-Nigrosin)',
  'Genetic Integrity': 'Immotile (%)',
  Morphology: 'Morphology (Strict Kruger, % normal)',
  'Forward Movement': 'Progressive Motility (PR, %)',
  'Sperm Count': 'Total Sperm Number (million/ejaculate)',
};

// ---------- util parsing ----------
// const onlyNumbers = (s) => (typeof s === 'string' ? s.replace(/[^\d.\-–]/g, '') : '');
const firstNumber = (s) => {
  if (!s) return null;
  const m = String(s).match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

// supports "0–90%", "0-100% (invert UI)"
const parseRangePair = (s, fallbackMin = 0, fallbackMax = 100) => {
  if (!s) return { min: fallbackMin, max: fallbackMax };
  const cleaned = String(s)
    .replace(/[^\d.\-–]/g, '')
    .replace(/–/g, '-');
  const parts = cleaned.split('-').filter(Boolean);
  const min = parts[0] !== undefined ? parseFloat(parts[0]) : fallbackMin;
  const max = parts[1] !== undefined ? parseFloat(parts[1]) : fallbackMax;
  return {
    min: Number.isFinite(min) ? min : fallbackMin,
    max: Number.isFinite(max) ? max : fallbackMax,
  };
};

// supports "<42", ">60", "42–60"
const parseBand = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  if (/</.test(str)) return { type: 'lt', a: firstNumber(str) };
  if (/>/.test(str)) return { type: 'gt', a: firstNumber(str) };
  const { min, max } = parseRangePair(str);
  if (Number.isFinite(min) && Number.isFinite(max)) return { type: 'between', a: min, b: max };
  const n = firstNumber(str);
  return n !== null ? { type: 'gte', a: n } : null; // fallback
};

// classify a value using JSON low/normal/great bands
const classifyByBands = (value, bands) => {
  const low = parseBand(bands.low);
  const normal = parseBand(bands.normal);
  const great = parseBand(bands.great);

  const inBand = (v, b) => {
    if (!b) return false;
    if (b.type === 'lt') return v < b.a;
    if (b.type === 'gt') return v > b.a;
    if (b.type === 'between') return v >= b.a && v <= b.b;
    if (b.type === 'gte') return v >= b.a;
    return false;
  };

  if (inBand(value, great)) return { text: 'Great', color: 'success' };
  if (inBand(value, normal)) return { text: 'Normal', color: 'success' };
  if (inBand(value, low)) return { text: 'Low', color: 'warning' };

  // fallback heuristic
  return value >= (normal?.a ?? 0) ? { text: 'Normal', color: 'success' } : { text: 'Low', color: 'warning' };
};

// derive a single numeric WHO cutoff (used for the vertical tick)
// prefer the first number in who_cutoff; if absent, use start of "normal" band
const deriveWhoCutoffNumber = (row) => {
  const n = firstNumber(row?.who_cutoff);
  if (n !== null) return n;
  const normal = parseBand(row?.normal);
  if (normal?.type === 'between') return normal.a;
  return null;
};

// ---------- MAIN ----------
const Overview = ({ currentTest, previousTest, userName }) => {
  const score = currentTest.overallScore;
  let interpretation;
  if (score < 12) {
    interpretation = {
      title: 'Below WHO minimum',
      description:
        'Parameters fall below WHO lower reference limits. Indicates significantly reduced semen quality. WHO minimum values score ≈12; anything under 12 is clinically concerning.',
      status: 'attention',
    };
  } else if (score === 12) {
    interpretation = {
      title: 'WHO minimum reference',
      description:
        'Represents exactly the WHO lower reference limits (≈12M TMSC, 30% PR, 4% morphology). Fertility is still possible, but this profile lies in the bottom 5th percentile of fertile men. Indicates: “Barely inside the normal range, but far from optimal”.',
      status: 'attention',
    };
  } else if (score > 12 && score < 40) {
    interpretation = {
      title: 'Below average',
      description:
        'One or more metrics near or slightly above WHO minimums. May indicate sub-optimal fertility potential. Often requires repeat testing to confirm.',
      status: 'attention',
    };
  } else if (score >= 40 && score < 60) {
    interpretation = {
      title: 'Average range',
      description: 'Typical fertile man. All parameters within normal clinical ranges. No severe impairments, but not unusually high.',
      status: 'normal',
    };
  } else if (score >= 60 && score < 80) {
    interpretation = {
      title: 'Good semen quality',
      description:
        'Consistently above-average values across most metrics. Strong TMSC, motility, and morphology. Represents healthy biological fertility potential.',
      status: 'optimal',
    };
  } else if (score >= 80 && score <= 95) {
    interpretation = {
      title: 'Excellent semen quality',
      description:
        'Very strong across all parameters. Fertility-relevant values well above population averages. Reflects biological plateau (we never score 100 due to diminishing returns).',
      status: 'optimal',
    };
  } else {
    interpretation = {
      title: 'Excellent semen quality',
      description: 'Near-maximum score reflecting outstanding parameters across all metrics.',
      status: 'optimal',
    };
  }

  const { keyMetrics = [] } = currentTest;
  const prevScore = previousTest && typeof previousTest.overallScore === 'number' ? previousTest.overallScore : null;

  // const prevDate = previousTest?.date ? `(${format(new Date(previousTest.date), 'MMM d, yyyy')})` : 'N/A';

  const currDate = currentTest.date ? `(${format(new Date(currentTest.date), 'MMM d, yyyy')})` : 'N/A';

  return (
    <div className="overview-container">
      {/* Summary */}
      <div className={`overview-summary ${interpretation.status}`}>
        <p className="section-label">YOUR FERTILITY OVERVIEW</p>
        <h2>
          {userName}, {interpretation.title}
        </h2>
        <p className="description">{interpretation.description}</p>
        <button className="learn-more">Understand your results →</button>
      </div>

      {/* SwimScore card */}
      <div className="score-card">
        <h3 className="score-title">OVERALL SWIMSCORE</h3>

        <div className="score-display">
          <span className={`score-value ${currentTest.overallScore >= 60 ? 'success' : currentTest.overallScore >= 40 ? 'warning' : 'danger'}`}>
            {currentTest.overallScore}
          </span>
          <span className="points-label">POINTS</span>
          {currentTest.trend && <TrendIndicator trend={currentTest.trend} />}
        </div>

        <p className="score-description">
          Your SwimScore combines six key fertility metrics into a single comprehensive score. WHO minimum reference is ≈12; higher scores reflect
          better semen quality across TMSC, motility, and morphology.
        </p>

        <SwimScoreTrack currentScore={currentTest.overallScore} previousScore={prevScore} />

        <div className="score-legend">
          <div className="legend-item">
            {/* <span className="legend-dot prev" /> */}
            {/* <span className="legend-text">Previous Test: {prevDate}</span> */}
          </div>
          <div className="legend-item">
            <span className="legend-dot curr" />
            <span className="legend-text">Current Test: {currDate}</span>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-section">
        <h3>KEY METRICS</h3>
        <div className="metrics-grid">
          {keyMetrics.map((metric) => {
            const jsonKey = jsonMetricKeyByDisplay[metric.name];
            const row = metricsData.find((m) => m.metric === jsonKey);

            // parse chart range (min/max)
            const { min: minRange, max: maxRange } = parseRangePair(row?.suggested_chart_range);

            // WHO cutoff tick
            const whoCutoffNum = deriveWhoCutoffNumber(row);

            // rating from JSON bands
            const rating = row
              ? classifyByBands(Number(metric.actualValue ?? metric.value), {
                  low: row.low,
                  normal: row.normal,
                  great: row.great,
                })
              : { text: '', color: '' };

            // visual config (colors/descriptions kept from your original helper)
            const cardColorClass = (() => {
              switch (rating.color) {
                case 'success':
                  return 'rating-success';
                case 'warning':
                  return 'rating-warning';
                default:
                  return '';
              }
            })();

            return (
              <div className="metric-card dash-card" key={metric.name}>
                <div className="metric-header">
                  <div className="metric-header-left">
                    <div className="title d-flex align-items-center gap-2">
                      <h4>
                        {metric.name}
                        {metric.officialName ? ` (${metric.officialName})` : ''}
                      </h4>
                      <button className="info-button" aria-label="Information">
                        <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M7.65406 5.31701C7.48073 5.31701 7.33406 5.25701 7.21406 5.13701C7.09406 5.01701 7.03406 4.87034 7.03406 4.69701C7.03406 4.52368 7.09406 4.37701 7.21406 4.25701C7.33406 4.13701 7.48073 4.07701 7.65406 4.07701C7.82073 4.07701 7.96073 4.13701 8.07406 4.25701C8.19406 4.37701 8.25406 4.52368 8.25406 4.69701C8.25406 4.87034 8.19406 5.01701 8.07406 5.13701C7.96073 5.25701 7.82073 5.31701 7.65406 5.31701ZM8.09406 6.20701V11.687H7.18406V6.20701H8.09406Z"
                            fill="white"
                          />
                          <path
                            d="M7.48145 0.357422C11.4288 0.357422 14.6064 3.4386 14.6064 7.21191C14.6062 10.9851 11.4286 14.0664 7.48145 14.0664C3.53441 14.0662 0.357633 10.985 0.357422 7.21191C0.357422 3.4387 3.53428 0.357581 7.48145 0.357422Z"
                            stroke="white"
                            strokeWidth="0.714286"
                          />
                        </svg>
                      </button>
                    </div>
                    <p>Progressive Motile Sperm (TMC)</p>
                  </div>

                  <div className="metric-header-right">
                    <p className="score">35</p>
                    <p className="rate"> WHO: 7</p>
                  </div>
                  <div className={`${cardColorClass} d-none`} />
                </div>

                {metric.description && <p className="metric-description">{metric.description}</p>}

                <div className="metric-values">
                  <div>
                    <span className="metric-label">Value</span>
                    <div className="metric-number">
                      <span className="value">{metric.value}</span>
                      <span className="unit">{metric.unit}</span>
                    </div>
                  </div>
                  <div className="metric-rating">
                    <span className="metric-label">Rating</span>
                    <span className={`rating ${rating.color}`}>{rating.text}</span>
                  </div>
                </div>

                {/* Unified reusable track for metrics */}
                <div className="metric-scale">
                  {(() => {
                    const val = Number(metric.actualValue ?? metric.value);
                    return (
                      <SwimScoreTrack
                        currentScore={val}
                        previousScore={null}
                        min={Number.isFinite(minRange) ? minRange : 0}
                        max={Number.isFinite(maxRange) ? maxRange : 100}
                        whoCutoff={whoCutoffNum ?? undefined}
                        showEdges={false}
                        showCurrentNumber={false}
                        showPreviousNumber={false}
                        heightPx={14}
                      />
                    );
                  })()}

                  {/* range labels */}
                  <div className="track-limits">
                    <span>{Number.isFinite(minRange) ? minRange : 0}</span>
                    <span>{Number.isFinite(maxRange) ? maxRange : 100}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

Overview.propTypes = {
  userName: PropTypes.string.isRequired,
  currentTest: PropTypes.shape({
    overallScore: PropTypes.number.isRequired,
    trend: PropTypes.string,
    date: PropTypes.string,
    keyMetrics: PropTypes.array,
  }).isRequired,
  previousTest: PropTypes.shape({
    overallScore: PropTypes.number,
    date: PropTypes.string,
  }),
};

export default Overview;
